import React, { useCallback, useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Plus, Edit2, Trash2, X, Save } from 'lucide-react';
import { api, hasPermission, ROLE_LABELS } from '../lib/api';

const ROLE_OPTIONS = [
  { value: 'super_admin', label: 'Super Admin' },
  { value: 'admin', label: 'Admin' },
  { value: 'editor', label: 'Editor' },
  { value: 'author', label: 'Author' },
];

const emptyForm = () => ({
  email: '',
  name: '',
  role: 'author',
  active: true,
  password: '',
});

const emptyFormErrors = () => ({
  email: '',
  name: '',
  password: '',
});

const NAME_PATTERN = /^[A-Za-z][A-Za-z .'-]{1,79}$/;
const NAME_ALLOWED_CHARS = /^[A-Za-z .'-]+$/;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const formatLastLogin = (value) => (value ? new Date(value).toLocaleString() : 'Never');

const AdminUsers = () => {
  const { user: currentUser } = useOutletContext();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | <user>
  const [form, setForm] = useState(emptyForm());
  const [formErrors, setFormErrors] = useState(emptyFormErrors());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const canCreate = hasPermission('user:create');
  const canEditAny = hasPermission('user:update');
  const canDelete = hasPermission('user:delete');

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/users', { params: { per_page: 100 } });
      setUsers(res.data.items);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    document.title = 'Admin - Users | ReCircle Foundation';
    fetchUsers();
  }, [fetchUsers]);

  const openNew = () => {
    setEditing('new');
    setForm(emptyForm());
    setFormErrors(emptyFormErrors());
    setError('');
  };

  const openEdit = (u) => {
    setEditing(u);
    setForm({
      email: u.email,
      name: u.name,
      role: u.role,
      active: u.active,
      password: '',
    });
    setFormErrors(emptyFormErrors());
    setError('');
  };

  const close = () => {
    setEditing(null);
    setForm(emptyForm());
    setFormErrors(emptyFormErrors());
    setError('');
  };

  const validateField = (field, value, mode = editing) => {
    if (field === 'name') {
      const trimmed = value.trim();
      if (!trimmed) return 'Full name is required.';
      if (!NAME_PATTERN.test(trimmed)) return 'Use 2-80 letters, spaces, apostrophes, hyphens, or periods.';
      return '';
    }

    if (field === 'email') {
      const trimmed = value.trim();
      if (!trimmed) return 'Email is required.';
      if (trimmed.length > 254) return 'Email must be 254 characters or fewer.';
      if (!EMAIL_PATTERN.test(trimmed)) return 'Enter a valid email address.';
      return '';
    }

    if (field === 'password') {
      if (mode !== 'new' && !value) return '';
      if (!value) return 'Password is required.';
      if (value.length < 8) return 'Password must be at least 8 characters.';
      if (value.length > 128) return 'Password must be 128 characters or fewer.';
      return '';
    }

    return '';
  };

  const validateForm = () => {
    const nextErrors = {
      name: validateField('name', form.name),
      email: validateField('email', form.email),
      password: validateField('password', form.password),
    };
    setFormErrors(nextErrors);
    return !Object.values(nextErrors).some(Boolean);
  };

  const updateFormField = (field, value) => {
    const nextValue = field === 'email' ? value.toLowerCase() : value;
    setForm((current) => ({ ...current, [field]: nextValue }));
    setFormErrors((current) => ({ ...current, [field]: validateField(field, nextValue) }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setError('');
    if (!validateForm()) return;
    setSaving(true);
    try {
      const cleanForm = {
        ...form,
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
      };
      if (editing === 'new') {
        await api.post('/users', cleanForm);
      } else {
        const payload = { ...cleanForm };
        if (!payload.password) delete payload.password;
        // Don't send unchanged email if same as before (avoid uniqueness conflict on case)
        await api.put(`/users/${editing.id}`, payload);
      }
      await fetchUsers();
      close();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (u) => {
    if (u.id === currentUser?.id) return;
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete user ${u.email}?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      fetchUsers();
    } catch (err) {
      // eslint-disable-next-line no-alert
      alert(err?.response?.data?.detail || 'Failed to delete user.');
    }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 xl:px-10 py-5 sm:py-7 xl:py-8" data-testid="admin-users-page">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-5 sm:mb-7 xl:mb-8">
        <div className="min-w-0 max-w-2xl">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 leading-tight">Team & access</h1>
          <p className="text-sm sm:text-base text-gray-600 mt-1 sm:mt-2 leading-relaxed">
            Manage team access.
          </p>
        </div>
        {canCreate && (
          <button
            type="button"
            onClick={openNew}
            data-testid="admin-new-user-btn"
            className="inline-flex w-full sm:w-auto sm:self-end sm:max-w-[250px] items-center justify-center gap-2 px-4 py-2 bg-brand-blue text-white rounded-md hover:opacity-90 transition-opacity"
          >
            <Plus size={16} /> New User
          </button>
        )}
      </div>

      <div>
        {loading ? (
          <div className="rounded-lg bg-white border border-gray-200 p-6 sm:p-8 text-gray-500">Loading...</div>
        ) : users.length === 0 ? (
          <div className="rounded-lg bg-white border border-gray-200 p-8 sm:p-12 text-center text-gray-500">No users.</div>
        ) : (
          <>
          <div className="hidden xl:block overflow-x-auto bg-white rounded-lg shadow-sm border border-gray-200">
            <table className="min-w-full text-sm" data-testid="admin-users-table">
              <thead className="bg-gray-50 border-b border-gray-200 text-left">
                <tr>
                  <th className="px-4 py-3 font-medium text-gray-700">Name</th>
                  <th className="px-4 py-3 font-medium text-gray-700">Email</th>
                  <th className="px-4 py-3 font-medium text-gray-700">Role</th>
                  <th className="px-4 py-3 font-medium text-gray-700">Status</th>
                  <th className="px-4 py-3 font-medium text-gray-700">Last login</th>
                  <th className="px-4 py-3 font-medium text-gray-700 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`admin-user-row-${u.email}`}>
                    <td className="px-4 py-3 font-medium text-gray-900">
                      {u.name}
                      {u.id === currentUser?.id && (
                        <span className="ml-2 text-xs text-brand-blue">(you)</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-700">{u.email}</td>
                    <td className="px-4 py-3 text-gray-700">{ROLE_LABELS[u.role] || u.role}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        u.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                      }`}>
                        {u.active ? 'Active' : 'Disabled'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-500">
                      {formatLastLogin(u.last_login_at)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-1">
                        {canEditAny && (
                          <button
                            type="button"
                            onClick={() => openEdit(u)}
                            className="p-2 rounded hover:bg-gray-100 text-gray-500 hover:text-brand-blue"
                            title="Edit"
                            data-testid={`admin-edit-user-${u.email}`}
                          >
                            <Edit2 size={16} />
                          </button>
                        )}
                        {canDelete && u.id !== currentUser?.id && (
                          <button
                            type="button"
                            onClick={() => handleDelete(u)}
                            className="p-2 rounded hover:bg-gray-100 text-gray-500 hover:text-red-600"
                            title="Delete"
                            data-testid={`admin-delete-user-${u.email}`}
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="xl:hidden space-y-3" data-testid="admin-users-card-list">
            {users.map((u) => (
              <article
                key={u.id}
                className="rounded-lg border border-gray-200 bg-white p-4 sm:p-5 shadow-sm"
                data-testid={`admin-user-card-${u.email}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <h2 className="font-semibold text-gray-900 leading-snug break-words">{u.name}</h2>
                      {u.id === currentUser?.id && (
                        <span className="text-xs font-medium text-brand-blue">you</span>
                      )}
                    </div>
                    <p className="mt-1 text-sm text-gray-600 break-all">{u.email}</p>
                  </div>
                  <span className={`shrink-0 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                    u.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                  }`}>
                    {u.active ? 'Active' : 'Disabled'}
                  </span>
                </div>

                <dl className="mt-4 grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <dt className="text-xs uppercase text-gray-500">Role</dt>
                    <dd className="mt-0.5 text-gray-900 break-words">{ROLE_LABELS[u.role] || u.role}</dd>
                  </div>
                  <div className="min-w-0">
                    <dt className="text-xs uppercase text-gray-500">Last login</dt>
                    <dd className="mt-0.5 text-gray-900 break-words">{formatLastLogin(u.last_login_at)}</dd>
                  </div>
                </dl>

                {(canEditAny || (canDelete && u.id !== currentUser?.id)) && (
                  <div className="mt-4 grid grid-cols-2 sm:flex sm:justify-end gap-2">
                    {canEditAny && (
                      <button
                        type="button"
                        onClick={() => openEdit(u)}
                        className="inline-flex w-full sm:w-auto sm:min-w-[108px] sm:max-w-[250px] items-center justify-center gap-2 px-3 py-2 rounded-md border border-gray-300 text-sm text-gray-700 hover:bg-gray-50"
                        data-testid={`admin-edit-user-card-${u.email}`}
                      >
                        <Edit2 size={15} /> Edit
                      </button>
                    )}
                    {canDelete && u.id !== currentUser?.id && (
                      <button
                        type="button"
                        onClick={() => handleDelete(u)}
                        className="inline-flex w-full sm:w-auto sm:min-w-[108px] sm:max-w-[250px] items-center justify-center gap-2 px-3 py-2 rounded-md border border-red-200 text-sm text-red-600 hover:bg-red-50"
                        data-testid={`admin-delete-user-card-${u.email}`}
                      >
                        <Trash2 size={15} /> Delete
                      </button>
                    )}
                  </div>
                )}
              </article>
            ))}
          </div>
          </>
        )}
      </div>

      {/* Edit / create modal */}
      {editing !== null && (
        <div className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4" onClick={close}>
          <form
            onSubmit={handleSave}
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-t-lg sm:rounded-lg shadow-xl w-full max-w-md max-h-[92vh] overflow-y-auto"
            data-testid="admin-user-form"
          >
            <div className="flex items-center justify-between p-5 border-b">
              <h2 className="text-lg font-semibold min-w-0 break-words pr-4">
                {editing === 'new' ? 'New user' : `Edit ${editing.name || editing.email}`}
              </h2>
              <button type="button" onClick={close} className="shrink-0 p-1 rounded hover:bg-gray-100 text-gray-500">
                <X size={18} />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Full name</label>
                <input
                  type="text"
                  value={form.name}
                  onBeforeInput={(e) => {
                    if (e.data && !NAME_ALLOWED_CHARS.test(e.data)) e.preventDefault();
                  }}
                  onPaste={(e) => {
                    const pasted = e.clipboardData.getData('text');
                    if (pasted && !NAME_ALLOWED_CHARS.test(pasted)) e.preventDefault();
                  }}
                  onChange={(e) => updateFormField('name', e.target.value)}
                  onBlur={(e) => updateFormField('name', e.target.value.trim())}
                  data-testid="user-form-name"
                  autoComplete="name"
                  maxLength={80}
                  aria-invalid={Boolean(formErrors.name)}
                  aria-describedby={formErrors.name ? 'user-form-name-error' : undefined}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                    formErrors.name ? 'border-red-400 focus:ring-red-200' : 'border-gray-300 focus:ring-brand-blue'
                  }`}
                  required
                />
                {formErrors.name && (
                  <p id="user-form-name-error" className="mt-1 text-xs text-red-600" data-testid="user-form-name-error">
                    {formErrors.name}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => updateFormField('email', e.target.value)}
                  onBlur={(e) => updateFormField('email', e.target.value.trim())}
                  data-testid="user-form-email"
                  autoComplete="email"
                  inputMode="email"
                  maxLength={254}
                  aria-invalid={Boolean(formErrors.email)}
                  aria-describedby={formErrors.email ? 'user-form-email-error' : undefined}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                    formErrors.email ? 'border-red-400 focus:ring-red-200' : 'border-gray-300 focus:ring-brand-blue'
                  }`}
                  required
                />
                {formErrors.email && (
                  <p id="user-form-email-error" className="mt-1 text-xs text-red-600" data-testid="user-form-email-error">
                    {formErrors.email}
                  </p>
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                  <select
                    value={form.role}
                    onChange={(e) => setForm({ ...form, role: e.target.value })}
                    data-testid="user-form-role"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-blue"
                  >
                    {ROLE_OPTIONS
                      .filter((r) => currentUser?.role === 'super_admin' || r.value !== 'super_admin')
                      .map((r) => (
                        <option key={r.value} value={r.value}>{r.label}</option>
                      ))}
                  </select>
                </div>
                <div className="flex items-end">
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={form.active}
                      onChange={(e) => setForm({ ...form, active: e.target.checked })}
                      data-testid="user-form-active"
                    />
                    Active
                  </label>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {editing === 'new' ? 'Password' : 'New password (leave blank to keep current)'}
                </label>
                <input
                  type="password"
                  value={form.password}
                  onChange={(e) => updateFormField('password', e.target.value)}
                  data-testid="user-form-password"
                  minLength={editing === 'new' ? 8 : 0}
                  maxLength={128}
                  autoComplete={editing === 'new' ? 'new-password' : 'off'}
                  aria-invalid={Boolean(formErrors.password)}
                  aria-describedby={formErrors.password ? 'user-form-password-error' : undefined}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                    formErrors.password ? 'border-red-400 focus:ring-red-200' : 'border-gray-300 focus:ring-brand-blue'
                  }`}
                  required={editing === 'new'}
                />
                {formErrors.password && (
                  <p id="user-form-password-error" className="mt-1 text-xs text-red-600" data-testid="user-form-password-error">
                    {formErrors.password}
                  </p>
                )}
                <p className="mt-1 text-xs text-gray-500">Minimum 8 characters.</p>
              </div>
              {error && <p className="text-sm text-red-600" data-testid="user-form-error">{error}</p>}
            </div>
            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 p-5 border-t bg-gray-50 sm:rounded-b-lg">
              <button
                type="button"
                onClick={close}
                className="w-full sm:w-auto sm:max-w-[250px] px-4 py-2 text-gray-700 rounded-md hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                data-testid="user-form-submit"
                className="w-full sm:w-auto sm:max-w-[250px] inline-flex items-center justify-center gap-2 px-4 py-2 bg-brand-blue text-white rounded-md hover:opacity-90 disabled:opacity-60"
              >
                <Save size={14} /> {saving ? 'Saving...' : editing === 'new' ? 'Create user' : 'Save changes'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default AdminUsers;
